import { useState, type FormEvent } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { Search, UserPlus, Users } from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  Empty,
  FilterRow,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Table,
  useAuth,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import {
  formatDate,
  fullName,
  USER_STATUS,
  type AdminUser,
  type Paginated,
} from '../../lib/rbac-types'
import { useRoles } from './RolesPage'

export const USERS_KEY = ['admin', 'users'] as const
const PER_PAGE = 25

/** Comptes internes (TODO 4.6). Filtres conservés dans l'URL (partageables). */
export function UsersPage() {
  const { t } = useTranslation()
  const { session } = useAuth()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [inviting, setInviting] = useState(false)
  const roles = useRoles()

  const filters = {
    search: params.get('q') ?? '',
    status: params.get('statut') ?? '',
    roleId: params.get('profil') ?? '',
    page: Number(params.get('page') ?? 1) || 1,
  }
  const [search, setSearch] = useState(filters.search)

  const setFilter = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value) next.set(key, value)
        else next.delete(key)
        if (key !== 'page') next.delete('page')
        return next
      },
      { replace: true }
    )

  const users = useQuery({
    queryKey: [...USERS_KEY, filters],
    queryFn: () => {
      const query = new URLSearchParams({ page: String(filters.page), perPage: String(PER_PAGE) })
      if (filters.search) query.set('search', filters.search)
      if (filters.status) query.set('status', filters.status)
      if (filters.roleId) query.set('roleId', filters.roleId)
      return session.api.get<Paginated<AdminUser>>(`/admin/users?${query}`)
    },
    placeholderData: keepPreviousData,
  })

  const columns: Column<AdminUser>[] = [
    {
      key: 'name',
      header: t('rbac.users.name'),
      cell: (u) => (
        <div>
          <div style={{ fontWeight: 600 }}>{fullName(u)}</div>
          <span className="text-muted" style={{ fontSize: 'var(--text-sm)' }}>
            {u.email}
          </span>
        </div>
      ),
    },
    {
      key: 'roles',
      header: t('rbac.users.roles'),
      cell: (u) =>
        u.roles?.length ? (
          <span className="row" style={{ gap: 'var(--space-1)', flexWrap: 'wrap' }}>
            {u.roles.map((role, index) => (
              <Badge key={`${role.code}-${index}`} variant="outline">
                {role.label}
              </Badge>
            ))}
          </span>
        ) : (
          <span className="text-muted">{t('rbac.users.noRole')}</span>
        ),
    },
    {
      key: 'status',
      header: t('rbac.users.status'),
      cell: (u) => (
        <Badge variant={USER_STATUS[u.status].variant}>{USER_STATUS[u.status].label}</Badge>
      ),
    },
    {
      key: 'mfa',
      header: t('rbac.users.mfa'),
      cell: (u) => (u.mfaEnabled ? <Badge variant="success">MFA</Badge> : '—'),
    },
    { key: 'lastLogin', header: t('rbac.users.lastLogin'), cell: (u) => formatDate(u.lastLoginAt) },
  ]

  return (
    <>
      <PageHeader
        eyebrow={t('rbac.roles.eyebrow')}
        title={t('rbac.users.title')}
        description={t('rbac.users.description')}
        actions={
          <Can perm="users.create">
            <Button variant="primary" icon={<UserPlus />} onClick={() => setInviting(true)}>
              {t('rbac.users.invite')}
            </Button>
          </Can>
        }
      />
      <Card>
        <FilterRow>
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault()
              setFilter('q', search.trim())
            }}
            className="row"
            style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}
          >
            <Input
              label={t('rbac.users.search')}
              placeholder={t('rbac.users.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              type="submit"
              variant="secondary"
              icon={<Search />}
              iconOnly
              aria-label={t('rbac.users.search')}
            />
          </form>
          <Select
            label={t('rbac.users.status')}
            value={filters.status}
            onChange={(e) => setFilter('statut', e.target.value)}
            options={[
              { value: '', label: t('rbac.users.all') },
              ...Object.entries(USER_STATUS).map(([value, { label }]) => ({ value, label })),
            ]}
          />
          <Select
            label={t('rbac.users.role')}
            value={filters.roleId}
            onChange={(e) => setFilter('profil', e.target.value)}
            options={[
              { value: '', label: t('rbac.users.allRoles') },
              ...(roles.data ?? []).map((role) => ({ value: role.id, label: role.label })),
            ]}
          />
        </FilterRow>
        {users.isError && <Alert tone="danger">{errorMessage(users.error)}</Alert>}
        <Table
          columns={columns}
          rows={users.data?.data ?? []}
          rowKey={(u) => u.id}
          loading={users.isPending}
          caption={t('rbac.users.title')}
          onRowClick={(u) => navigate(`/utilisateurs/${u.id}`)}
          empty={<Empty icon={<Users aria-hidden="true" />} title={t('rbac.users.empty')} />}
        />
        {users.data && users.data.meta.total > PER_PAGE && (
          <Pagination
            page={users.data.meta.currentPage}
            perPage={users.data.meta.perPage}
            total={users.data.meta.total}
            onPageChange={(page) => setFilter('page', String(page))}
          />
        )}
      </Card>
      <InviteModal open={inviting} onClose={() => setInviting(false)} />
    </>
  )
}

function InviteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const empty = { firstName: '', lastName: '', email: '', phone: '' }
  const [form, setForm] = useState(empty)

  const invite = useMutation({
    mutationFn: () =>
      session.api.post<AdminUser>('/admin/users', {
        ...form,
        phone: form.phone.trim() || undefined,
      }),
    onSuccess: async (user) => {
      toast.success(t('rbac.users.sent'), user.email)
      await queryClient.invalidateQueries({ queryKey: USERS_KEY })
      setForm(empty)
      onClose()
      navigate(`/utilisateurs/${user.id}`)
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(invite.error)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    invite.mutate()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('rbac.users.inviteTitle')}
      description={t('rbac.users.inviteText')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('rbac.create.cancel')}
          </Button>
          <Button variant="primary" type="submit" form="invite-user" loading={invite.isPending}>
            {t('rbac.users.send')}
          </Button>
        </>
      }
    >
      <form id="invite-user" className="stack" onSubmit={submit}>
        <Input
          label={t('rbac.users.firstName')}
          required
          autoComplete="off"
          value={form.firstName}
          error={errors.firstName}
          onChange={(e) => setForm({ ...form, firstName: e.target.value })}
        />
        <Input
          label={t('rbac.users.lastName')}
          required
          autoComplete="off"
          value={form.lastName}
          error={errors.lastName}
          onChange={(e) => setForm({ ...form, lastName: e.target.value })}
        />
        <Input
          label={t('rbac.users.email')}
          type="email"
          required
          autoComplete="off"
          value={form.email}
          error={errors.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Input
          label={t('rbac.users.phone')}
          type="tel"
          autoComplete="off"
          value={form.phone}
          error={errors.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </form>
    </Modal>
  )
}
